import { z } from 'zod';

/**
 * The cancellation policy pinned against a hotel booking.
 *
 * Extracted from `hotel-booking.ts` into a leaf module with no imports of its
 * own, so `travel-cancellation.ts` can describe a cancellation quote in terms of
 * a policy band without the two files importing each other. A cycle between Zod
 * schema modules is not a lint nit: the schemas are evaluated at module load, so
 * whichever side loses the race sees `undefined` where a schema should be and
 * throws inside `z.object()` at import time, long before any request arrives.
 *
 * `hotel-booking.ts` re-exports both symbols, so every existing import path
 * keeps working and there is still exactly one definition.
 */

/**
 * One date band of a rate's cancellation policy.
 *
 * TBO returns these as `CancelPolicies[]` with a `FromDate`, a `ChargeType` and
 * a `CancellationCharge`. Rendered as a plain-language timeline, this is the
 * single most important object in the hotel funnel: it is what the guest agrees
 * to, and a rate flipping from free-cancellation to non-refundable at an
 * unchanged price is the case that generates chargebacks.
 */
export const HotelCancellationBandSchema = z.object({
  /** ISO date-time this band starts applying. */
  fromDate: z.string(),
  /** ISO date-time this band stops applying. Null on the final, open band. */
  toDate: z.string().nullable().default(null),
  /**
   * `Percentage` — `charge` is a percent of the stay.
   * `Fixed` — `charge` is an amount in the supplier's currency.
   * `Nights` — `charge` is a night count.
   */
  chargeType: z.enum(['Percentage', 'Fixed', 'Nights', 'Unknown']).default('Unknown'),
  charge: z.number(),
  /**
   * The band's charge expressed in the sell currency's minor units, computed
   * server-side so the UI never has to reason about percent-of-what.
   *
   * Null means "we could not read the supplier's charge type", NOT "free". Any
   * consumer that treats a null as zero is quoting a refund we cannot honour.
   */
  chargePaise: z
    .string()
    .regex(/^-?\d+$/)
    .nullable()
    .default(null),
});
export type HotelCancellationBand = z.infer<typeof HotelCancellationBandSchema>;

export const HotelCancellationPolicySchema = z.object({
  bands: z.array(HotelCancellationBandSchema).default([]),
  /** Free-cancellation deadline, when the rate publishes one. */
  lastCancellationDate: z.string().nullable().default(null),
  /** False when every band charges from the moment of booking. */
  isRefundable: z.boolean().default(false),
  /** Supplier's own prose, sanitised. Shown under the timeline. */
  note: z.string().nullable().default(null),
});
export type HotelCancellationPolicy = z.infer<typeof HotelCancellationPolicySchema>;
